import { useExplorerStore } from "../store/explorerStore";
import { useCatalogueLabels } from "../hooks/useCatalogueLabels";
import { describeGroup, describeRule, isGroup } from "../lib/filterLogic";
import { FilterTreePreview } from "./FilterTreePreview";
import type { FilterGroup, FilterRule } from "../types/inpact";

export function ActiveFilterChips() {
  const { filterGroup, resetFilters } = useExplorerStore();
  const catalogueLabels = useCatalogueLabels();
  const entries = filterGroup.rules;
  if (entries.length === 0) return null;

  // Render a chip for every top-level entry — flat rules AND nested groups —
  // so an advanced filter with a nested AND/OR group stays removable from
  // the chip bar. Each branch is its own single-responsibility component
  // (30-line function budget); this callback is just the dispatch.
  return (
    <div className="active-filters" role="group" aria-label="Active filters">
      {entries.map((entry, i) =>
        isGroup(entry) ? (
          <GroupChip key={`chip-${i}`} group={entry} index={i} catalogueLabels={catalogueLabels} />
        ) : (
          <RuleChip key={`chip-${i}`} rule={entry} index={i} catalogueLabels={catalogueLabels} />
        ),
      )}
      <button
        type="button"
        className="active-filters__clear"
        onClick={resetFilters}
      >
        Clear all
      </button>
    </div>
  );
}

/** Chip for a nested AND/OR sub-group. Expands into a faithful
 *  FilterTreePreview (instead of just a vague "N conditions" count) so
 *  users can see exactly what they're about to remove, and stays removable
 *  via the sibling `×` button — same semantics as a flat-rule chip. */
function GroupChip({ group, index, catalogueLabels }: {
  group: FilterGroup;
  index: number;
  catalogueLabels: Record<string, string>;
}) {
  const removeRuleAt = useExplorerStore((s) => s.removeRuleAt);
  const label = describeGroup(group);
  return (
    <span className="chip chip--group">
      <details className="chip__details">
        <summary className="chip__summary">{label}</summary>
        <FilterTreePreview group={group} catalogueLabels={catalogueLabels} />
      </details>
      <button
        type="button"
        className="chip__close"
        onClick={() => removeRuleAt([index])}
        aria-label={`Remove filter: ${label}`}
      >
        ×
      </button>
    </span>
  );
}

/** Chip for a single flat rule. */
function RuleChip({ rule, index, catalogueLabels }: {
  rule: FilterRule;
  index: number;
  catalogueLabels: Record<string, string>;
}) {
  const removeRuleAt = useExplorerStore((s) => s.removeRuleAt);
  const label = describeRule(rule, catalogueLabels);
  return (
    <button
      type="button"
      className="chip"
      onClick={() => removeRuleAt([index])}
      aria-label={`Remove filter: ${label}`}
    >
      {label}
      <span className="chip__close" aria-hidden="true">×</span>
    </button>
  );
}
